/**
 * Image Compression Utility
 * Compresses images before upload to reduce payload size
 */

const MAX_WIDTH = 1200;
const MAX_HEIGHT = 1200;
const JPEG_QUALITY = 0.5;
const MAX_FILE_SIZE = 500 * 1024; // Target 500KB max per image

/**
 * Compress an image file
 * @param {File} file - The image file to compress
 * @param {Object} options - Compression options
 * @returns {Promise<File>} - Compressed file
 */
export const compressImage = async (file, options = {}) => {
  const {
    maxWidth = MAX_WIDTH,
    maxHeight = MAX_HEIGHT,
    quality = JPEG_QUALITY,
    maxSize = MAX_FILE_SIZE
  } = options;

  // Skip non-image files
  if (!file.type.startsWith('image/')) {
    return file;
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.onload = () => {
      try {
        // Calculate new dimensions maintaining aspect ratio
        let { width, height } = img;
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }

        canvas.width = width;
        canvas.height = height;

        // Draw image on canvas with white background
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        // Try progressive quality reduction if needed
        let currentQuality = quality;
        
        const tryCompress = () => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                resolve(file); // Return original if compression fails
                return;
              }

              // If still too large and quality can be reduced further
              if (blob.size > maxSize && currentQuality > 0.2) {
                currentQuality -= 0.1;
                tryCompress();
                return;
              }

              // Create new file with .jpg extension
              const newName = file.name.replace(/\.[^.]+$/, '.jpg');
              const compressedFile = new File(
                [blob],
                newName,
                { type: 'image/jpeg', lastModified: Date.now() }
              );

              const reduction = ((1 - compressedFile.size / file.size) * 100).toFixed(0);
              console.log(
                `✓ ${file.name}: ${(file.size / 1024).toFixed(0)}KB → ${(compressedFile.size / 1024).toFixed(0)}KB (-${reduction}%)`
              );

              resolve(compressedFile);
            },
            'image/jpeg',
            currentQuality
          );
        };

        tryCompress();
      } catch (error) {
        console.error('Compression error:', error);
        resolve(file); // Return original on error
      }
    };

    img.onerror = () => {
      console.warn('Could not load image for compression:', file.name);
      resolve(file); // Return original if can't load
    };

    // Load image from file
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target.result;
    };
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });
};

/**
 * Compress multiple files
 * @param {File[]} files - Array of files to compress
 * @param {Function} onProgress - Progress callback (current, total)
 * @returns {Promise<File[]>} - Array of compressed files
 */
export const compressFiles = async (files, onProgress = null) => {
  const compressed = [];
  
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const compressedFile = await compressImage(file);
    compressed.push(compressedFile);
    
    if (onProgress) {
      onProgress(i + 1, files.length);
    }
  }
  
  return compressed;
};

/**
 * Get total size of files
 * @param {File[]} files - Array of files
 * @returns {number} - Total size in bytes
 */
export const getTotalSize = (files) => {
  return files.reduce((total, file) => total + file.size, 0);
};

/**
 * Format file size for display
 * @param {number} bytes - Size in bytes
 * @returns {string} - Formatted size string
 */
export const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default {
  compressImage,
  compressFiles,
  getTotalSize,
  formatFileSize
};
